import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { AccessRights } from '../../typings/common';
import { InternalProductPlace } from '../../typings/internalProductPlace';
import { ACCESS_SELECTORS, MarketerProduct, MarketersPageStates, ServerMarketerAccess } from '../../typings/marketers';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { EditableCell } from '../EditableCell/EditableCell';
import { PopupDecorator } from '../PopupDecorator/PopupDecorator';
import { SelectDecorator, SelectItem } from '../SelectDecorator/SelectDecorator';

import './MarketerProductAccessCell.css';
import * as keyset from './MarketerProductAccessCell.i18n';

const i18n = i18nFactory(keyset);

const BLOCK = 'marketer-product-access-cell';

export interface MarketerProductAccessCellProps {
    product: MarketerProduct;
    places: InternalProductPlace[];
    pageStatus: MarketersPageStates;
    isGridScrolling: boolean;
    accessRights: AccessRights;
    onSave: (accessType: ServerMarketerAccess, placeIds: number[]) => void;
    onEdit: () => void;
    onCancel: () => void;
}

interface State {
    accessType: ServerMarketerAccess;
    placeIds: string[];
    editing: boolean;
}

export class MarketerProductAccessCell extends React.Component<MarketerProductAccessCellProps, State> {
    readonly state = {
        accessType: ServerMarketerAccess.READONLY,
        placeIds: [],
        editing: false
    };

    private readonly placesPositions: Map<number, number> = new Map<number, number>();
    private readonly placesOptions: SelectItem[];

    constructor(props: MarketerProductAccessCellProps) {
        super(props);

        const { places } = this.props;

        for (const { id } of places) {
            this.placesPositions.set(id, this.placesPositions.size);
        }

        this.placesOptions = places.map(place => ({
            val: String(place.id),
            text: `${place.id} - ${place.fullDescription}`
        }));
    }

    public componentDidUpdate() {
        if (!this.state.editing) {
            return;
        }

        const {
            product: { isSaved },
            isGridScrolling,
            accessRights: { canEditInternalAdManagers }
        } = this.props;

        if (isSaved) {
            this.setState({
                editing: false
            });

            return;
        }

        if (isGridScrolling && canEditInternalAdManagers) {
            this.onCancel();
        }
    }

    render() {
        const {
            product: { access, placeIds: savedPlaceIds },
            accessRights: { canEditInternalAdManagers },
            isGridScrolling
        } = this.props;

        const accessText = (ACCESS_SELECTORS.find(item => item.val === access) || { text: '' }).text;

        if (!canEditInternalAdManagers) {
            return (
                <>
                    {accessText}
                    &nbsp;
                    {savedPlaceIds.sort(this.sortPlacesByPosition).join(', ')}
                </>
            );
        }

        const { editing, placeIds, accessType } = this.state;

        return (
            <>
                <EditableCell isActive={editing} onClick={this.onEdit}>
                    {accessText}
                </EditableCell>
                {savedPlaceIds.sort(this.sortPlacesByPosition).join(', ')}
                {editing && (
                    <PopupDecorator
                        anchor={this}
                        visible={!isGridScrolling}
                        target={'anchor'}
                        directions={['top-center']}
                        hasTail={true}
                        header={i18n('access')}
                        footer={this.renderFooter()}
                        onOutsideClick={this.onCancel}
                    >
                        <div className={Bem.cls(BLOCK, 'section')}>
                            <SelectDecorator
                                cls={Bem.cls(BLOCK, 'select')}
                                popup={{ hiding: false }}
                                size="s"
                                theme="normal"
                                width="max"
                                type="radio"
                                view="classic"
                                items={ACCESS_SELECTORS}
                                val={accessType}
                                onChange={this.onAccessChange}
                            />
                        </div>
                        {accessType === ServerMarketerAccess.BY_PLACE && (
                            <div className={Bem.cls(BLOCK, 'section')}>
                                <SelectDecorator
                                    cls={Bem.cls(BLOCK, 'select')}
                                    popup={{ hiding: false }}
                                    size="s"
                                    theme="normal"
                                    width="max"
                                    type="check"
                                    view="classic"
                                    items={this.placesOptions}
                                    val={placeIds}
                                    onChange={this.onPlaceChange}
                                />
                            </div>
                        )}
                    </PopupDecorator>
                )}
            </>
        );
    }

    private renderFooter = () => {
        const { pageStatus } = this.props;

        return (
            <>
                <ButtonDecorator
                    cls={Bem.cls(BLOCK, 'action')}
                    theme={'action'}
                    size={'s'}
                    text={i18n('save')}
                    onClick={this.onSave}
                    progress={pageStatus === MarketersPageStates.ACTION}
                    disabled={!this.hasChanges()}
                />
                <ButtonDecorator
                    theme={'normal'}
                    size={'s'}
                    text={i18n('cancel')}
                    onClick={this.onCancel}
                    disabled={pageStatus === MarketersPageStates.ACTION}
                />
            </>
        );
    };

    private hasChanges = (): boolean => {
        const {
            product: { access, placeIds }
        } = this.props;
        const changedAccess = access !== this.state.accessType;

        if (this.state.accessType === ServerMarketerAccess.BY_PLACE) {
            const selectedPlaceIds = this.state.placeIds;

            return changedAccess
                ? selectedPlaceIds.length > 0
                : placeIds.sort().join('') !== selectedPlaceIds.sort().join('');
        }

        return changedAccess;
    };

    private onEdit = () => {
        const {
            onEdit,
            product: { access, placeIds }
        } = this.props;

        this.setState({
            editing: true,
            accessType: access,
            placeIds: placeIds.map(String)
        });

        onEdit();
    };

    private onCancel = () => {
        const {
            onCancel,
            product: { access, placeIds }
        } = this.props;

        this.setState({
            editing: false,
            accessType: access,
            placeIds: placeIds.map(String)
        });

        onCancel();
    };

    private onAccessChange = (access: ServerMarketerAccess[]) => {
        if (access.length) {
            this.setState({
                accessType: access[0]
            });
        }
    };

    private onPlaceChange = (placeIds: string[]) => {
        this.setState({
            placeIds
        });
    };

    private onSave = () => {
        const { onSave } = this.props;

        if (this.hasChanges()) {
            const { placeIds, accessType } = this.state;

            onSave(accessType, placeIds.map(Number));
        }
    };

    private sortPlacesByPosition = (placeId1: number, placeId2: number) => {
        const position1 = this.placesPositions.get(placeId1);
        const position2 = this.placesPositions.get(placeId2);

        if (position1 === undefined || position2 === undefined) {
            return 0;
        }

        // Сортируем для более естественного поведения при выборе элементов,
        // чтобы результат выбора был в том же порядке, что и элементы выпадушки
        return position1 - position2;
    };
}
